import { fetchBaseQuery, type BaseQueryApi, type FetchArgs } from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import { logout, sessionRefreshed } from '../features/auth/slice.ts';
import { refreshOAuthTokens, enqueueBackgroundTokenRefresh } from './tokenService.ts';

const mutex = new Mutex();

const REFRESH_BUFFER_SECONDS = 60;

function isOAuthTokenRequest(args: string | FetchArgs): boolean {
  const url = typeof args === 'string' ? args : (args.url ?? '');
  return (
    url.includes('/oauth/token') ||
    url.includes('/umbraco/api/SpaAuth/Login') ||
    url.includes('/umbraco/api/SpaAuth/Refresh')
  );
}

const prepareHeaders = async (headers: Headers, { body }: any) => {
  const token = localStorage.getItem('access_token');
  const tokenExpiry = localStorage.getItem('token_expiry');

  if (token && tokenExpiry) {
    const currentTime = Math.floor(Date.now() / 1000);
    const expiryTime = parseInt(tokenExpiry, 10);

    if (currentTime >= expiryTime) {
      try {
        await refreshOAuthTokens();
      } catch {
        /* let the request proceed; 401 handler may refresh or logout */
      }
    } else if (expiryTime - currentTime < REFRESH_BUFFER_SECONDS) {
      enqueueBackgroundTokenRefresh();
    }

    const latestToken = localStorage.getItem('access_token');
    const latestExpiry = localStorage.getItem('token_expiry');
    if (latestToken && latestExpiry) {
      const now = Math.floor(Date.now() / 1000);
      if (now < parseInt(latestExpiry, 10)) {
        headers.set('authorization', `Bearer ${latestToken}`);
      }
    }
  }

  const hasBody = body != null;
  const isFormData = body instanceof FormData;

  if (isFormData) {
    headers.delete('content-type');
  } else if (hasBody) {
    headers.set('content-type', 'application/json');
  } else {
    headers.delete('content-type');
  }

  const antiForgeryToken =
    typeof document === 'undefined'
      ? undefined
      : document.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]')?.value;
  if (antiForgeryToken) headers.set('RequestVerificationToken', antiForgeryToken);

  return headers;
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL || '',
  credentials: 'include',
  prepareHeaders,
});

/**
 * RTK Query base query: proactive refresh (aligned with axios), mutex on 401,
 * retries once after refresh. Logs out only when refresh fails.
 */
export const baseQuery = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: Parameters<typeof rawBaseQuery>[2]
) => {
  await mutex.waitForUnlock();
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401 && !isOAuthTokenRequest(args)) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        try {
          const tokenResponse = await refreshOAuthTokens();
          api.dispatch(
            sessionRefreshed({
              accessToken: tokenResponse.access_token,
              homePageUrl: tokenResponse.home_page_url,
              logoPageUrl: tokenResponse.logo_page_url,
            })
          );
          result = await rawBaseQuery(args, api, extraOptions);
        } catch {
          api.dispatch(logout());
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await rawBaseQuery(args, api, extraOptions);
    }
  }

  return result;
};
